import { useCallback, useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetInvitesQuery } from '../../store/auth/auth'
import { useGetUsersQuery } from '../../store/user/user'
import { type InviteChipKey, type MemberChipKey, type TeamChipKey, isInviteChip } from './teamChips'
import { type TeamQueryFilters, inviteCountFilter, inviteFilter, memberCountArgs, memberListArgs } from './teamQueries'
import { type TeamRow, toInviteRow, toMemberRow } from './teamRows'

const COUNT_PAGE = { page: 0, size: 1 }

export interface UseTeamListArgs extends TeamQueryFilters {
  chip: TeamChipKey
  page: number
  perPage: number
  hasInviteScope: boolean
}

export interface UseTeamListResult {
  rows: TeamRow[]
  totalRows: number
  isFetching: boolean
  counts: Partial<Record<TeamChipKey, number>>
  refetch: () => void
}

export const useTeamList = ({ chip, page, perPage, hasInviteScope, namePattern, roleGroupNames }: UseTeamListArgs): UseTeamListResult => {
  const accountId = useAppSelector(selectAccountId)
  const showsInvites = isInviteChip(chip)
  const showsGroups = chip === 'requested'
  const filters = useMemo<TeamQueryFilters>(() => ({ namePattern, roleGroupNames }), [namePattern, roleGroupNames])

  const memberChip: MemberChipKey = chip === 'deactivated' ? 'deactivated' : 'active'
  const inviteChip: InviteChipKey = showsInvites ? chip : 'pending'

  const {
    data: memberPage,
    isFetching: membersFetching,
    refetch: refetchMembers,
  } = useGetUsersQuery(memberListArgs(accountId, memberChip, { page, perPage }, filters), {
    skip: showsInvites,
    refetchOnMountOrArgChange: true,
  })

  const { data: invitePage, isFetching: invitesFetching } = useGetInvitesQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, filter: inviteFilter(inviteChip, filters) },
    { skip: !showsInvites || showsGroups, refetchOnMountOrArgChange: true },
  )

  const { data: activeCount, refetch: refetchActiveCount } = useGetUsersQuery(memberCountArgs(accountId, 'active'))

  const inviteCountArgs = (countChip: InviteChipKey) => ({ accountId, pageRequest: COUNT_PAGE, filter: inviteCountFilter(countChip) })
  const skipInviteCounts = { skip: !hasInviteScope }

  const { data: pendingCount } = useGetInvitesQuery(inviteCountArgs('pending'), skipInviteCounts)
  const { data: expiredCount } = useGetInvitesQuery(inviteCountArgs('expired'), skipInviteCounts)
  const { data: requestedCount } = useGetInvitesQuery(inviteCountArgs('requested'), skipInviteCounts)

  const rows = useMemo<TeamRow[]>(
    () => (showsInvites ? (invitePage?.data ?? []).map(toInviteRow) : (memberPage?.data ?? []).map(toMemberRow)),
    [showsInvites, invitePage, memberPage],
  )

  const refetch = useCallback(() => {
    if (!showsInvites) {
      refetchMembers()
    }
    refetchActiveCount()
  }, [showsInvites, refetchMembers, refetchActiveCount])

  return {
    rows,
    totalRows: (showsInvites ? invitePage?.page.totalElements : memberPage?.page.totalElements) ?? 0,
    isFetching: showsInvites ? invitesFetching : membersFetching,
    counts: {
      active: activeCount?.page.totalElements,
      pending: pendingCount?.page.totalElements,
      expired: expiredCount?.page.totalElements,
      requested: requestedCount?.page.totalElements,
    },
    refetch,
  }
}
